import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, TrendingDown, Package, Users, DollarSign,
  Wallet, CreditCard, AlertTriangle, Calendar, Download,
  FileText, ArrowUpRight, ArrowDownRight, FileSpreadsheet, CheckCircle, Receipt
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ExportBar from '../../components/ExportBar';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const ReportsDashboard = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
  });

  const [salesSummary, setSalesSummary] = useState(null);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [partyOutstanding, setPartyOutstanding] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [cashBankSummary, setCashBankSummary] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('sales');

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [salesRes, purchaseRes, partyRes, stockRes, cashBankRes, expenseRes] = await Promise.all([
        reportsAPI.salesSummary(dateRange),
        reportsAPI.purchaseSummary(dateRange),
        reportsAPI.partyOutstanding(),
        reportsAPI.lowStock(),
        reportsAPI.cashBankSummary(),
        reportsAPI.expenseByCategory(dateRange),
      ]);

      setSalesSummary(salesRes.data);
      setPurchaseSummary(purchaseRes.data);
      setPartyOutstanding(partyRes.data || []);
      setLowStock(stockRes.data || []);
      setCashBankSummary(cashBankRes.data);
      setExpenseSummary(expenseRes.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load reports'));
    } finally {
      setLoading(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const tabs = [
    { id: 'sales', label: 'Sales', icon: TrendingUp },
    { id: 'purchases', label: 'Purchases', icon: TrendingDown },
    { id: 'parties', label: 'Parties', icon: Users },
    { id: 'stock', label: 'Stock', icon: Package },
    { id: 'cashbank', label: 'Cash & Bank', icon: Wallet },
    { id: 'gst', label: 'GST', icon: FileText },
    { id: 'expenses', label: 'Expenses', icon: Receipt },
  ];

  // Get current stats for header based on active tab
  const getHeaderStats = () => {
    switch (activeTab) {
      case 'sales':
        return [
          { icon: DollarSign, label: 'Total Sales', value: formatCompactCurrency(salesSummary?.totals?.total_sales), color: 'emerald' },
          { icon: FileText, label: 'Invoices', value: salesSummary?.totals?.invoice_count || 0, color: 'blue' },
          { icon: TrendingUp, label: 'Avg. Invoice', value: formatCompactCurrency(salesSummary?.totals?.invoice_count > 0 ? salesSummary?.totals?.total_sales / salesSummary?.totals?.invoice_count : 0), color: 'purple' },
          { icon: CreditCard, label: 'Tax Collected', value: formatCompactCurrency(salesSummary?.totals?.total_tax), color: 'amber' },
        ];
      case 'purchases':
        return [
          { icon: DollarSign, label: 'Total Purchases', value: formatCompactCurrency(purchaseSummary?.totals?.total_purchases), color: 'rose' },
          { icon: FileText, label: 'Invoices', value: purchaseSummary?.totals?.invoice_count || 0, color: 'blue' },
          { icon: TrendingDown, label: 'Avg. Purchase', value: formatCompactCurrency(purchaseSummary?.totals?.invoice_count > 0 ? purchaseSummary?.totals?.total_purchases / purchaseSummary?.totals?.invoice_count : 0), color: 'purple' },
        ];
      case 'parties':
        return [
          { icon: ArrowUpRight, label: 'Receivables', value: formatCompactCurrency(partyOutstanding.totals?.total_receivable), color: 'emerald' },
          { icon: ArrowDownRight, label: 'Payables', value: formatCompactCurrency(partyOutstanding.totals?.total_payable), color: 'rose' },
          { icon: DollarSign, label: 'Net Outstanding', value: formatCompactCurrency(partyOutstanding.totals?.net), color: 'blue' },
        ];
      case 'stock':
        return [
          { icon: AlertTriangle, label: 'Low Stock Items', value: lowStock.totals?.count || 0, color: 'amber' },
          { icon: Package, label: 'Total Items', value: lowStock.totals?.total_items || '—', color: 'slate' },
        ];
      case 'cashbank':
        return [
          { icon: Wallet, label: 'Cash in Hand', value: formatCompactCurrency(cashBankSummary?.totals?.cash_in_hand), color: 'emerald' },
          { icon: CreditCard, label: 'Bank Balance', value: formatCompactCurrency(cashBankSummary?.totals?.bank_balance), color: 'blue' },
          { icon: DollarSign, label: 'Total Balance', value: formatCompactCurrency(cashBankSummary?.totals?.total), color: 'purple' },
        ];
      case 'gst':
        return [
          { icon: FileText, label: 'GST Reports', value: 10, color: 'slate' },
          { icon: FileSpreadsheet, label: 'Returns', value: 3, color: 'blue' },
          { icon: BarChart3, label: 'Registers', value: 2, color: 'emerald' },
          { icon: DollarSign, label: 'Analysis', value: 5, color: 'purple' },
        ];
      case 'expenses':
        return [
          { icon: Receipt, label: 'Total Expenses', value: formatCompactCurrency(expenseSummary?.totals?.total_expenses), color: 'rose' },
          { icon: FileText, label: 'Categories', value: expenseSummary?.totals?.category_count || 0, color: 'blue' },
        ];
      default:
        return [];
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="reports-dashboard">
      {/* Premium Header */}
      <ListPageHeader
        title="Reports & Analytics"
        stats={getHeaderStats()}
        secondaryAction={{
          label: `${new Date(dateRange.from_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - ${new Date(dateRange.to_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Date Range & Tabs Section */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            {/* Tab Pills */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                      activeTab === tab.id 
                        ? 'bg-teal-600 text-white shadow-sm' 
                        : 'text-slate-600 hover:text-slate-800 hover:bg-white'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${activeTab === tab.id ? 'text-white' : 'text-slate-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Date Range Picker */}
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={dateRange.from_date}
                onChange={(e) => setDateRange({ ...dateRange, from_date: e.target.value })}
                className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none w-[100px]"
              />
              <span className="text-slate-300">→</span>
              <input
                type="date"
                value={dateRange.to_date}
                onChange={(e) => setDateRange({ ...dateRange, to_date: e.target.value })}
                className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none w-[100px]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading reports...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Sales Tab */}
            {activeTab === 'sales' && salesSummary && (
              <>
                <ExportBar 
                  reportType="sales-summary" 
                  dateRange={dateRange}
                  businessId={businessId}
                  reportName="Sales Summary"
                />
                <ReportTable
                  title="Date-wise Sales Breakdown"
                  columns={[
                    { key: 'date', label: 'Date', width: '40%' },
                    { key: 'invoice_count', label: 'Invoices', width: '30%', align: 'right' },
                    { key: 'total_sales', label: 'Total Sales', width: '30%', align: 'right', format: 'currency' },
                  ]}
                  rows={salesSummary.rows || []}
                  emptyMessage="No sales data for selected period"
                />
              </>
            )}

            {/* Purchases Tab */}
            {activeTab === 'purchases' && purchaseSummary && (
              <>
                <ExportBar 
                  reportType="purchase-summary" 
                  dateRange={dateRange}
                  businessId={businessId}
                  reportName="Purchase Summary"
                />
                <ReportTable
                  title="Date-wise Purchase Breakdown"
                  columns={[
                    { key: 'date', label: 'Date', width: '40%' },
                    { key: 'invoice_count', label: 'Invoices', width: '30%', align: 'right' },
                    { key: 'total_purchases', label: 'Total Purchases', width: '30%', align: 'right', format: 'currency' },
                  ]}
                  rows={purchaseSummary.rows || []}
                  emptyMessage="No purchase data for selected period"
                />
              </>
            )}

            {/* Parties Tab */}
            {activeTab === 'parties' && (
              <>
                <ExportBar 
                  reportType="party-outstanding" 
                  dateRange={dateRange}
                  businessId={businessId}
                  reportName="Party Outstanding"
                />
                <div className="grid grid-cols-2 gap-3">
                  <ReportTable
                    title="Receivables (To Collect)"
                    titleColor="emerald"
                    columns={[
                      { key: 'name', label: 'Party', width: '60%' },
                      { key: 'current_balance', label: 'Amount', width: '40%', align: 'right', format: 'currency', colorClass: 'text-emerald-600' },
                    ]}
                    rows={(partyOutstanding.receivables || []).slice(0, 10)}
                    emptyMessage="No receivables"
                    compact
                  />
                  <ReportTable
                    title="Payables (To Pay)"
                    titleColor="rose"
                    columns={[
                      { key: 'name', label: 'Party', width: '60%' },
                      { key: 'current_balance', label: 'Amount', width: '40%', align: 'right', format: 'currency', colorClass: 'text-rose-600', absolute: true },
                    ]}
                    rows={(partyOutstanding.payables || []).slice(0, 10)}
                    emptyMessage="No payables"
                    compact
                  />
                </div>
              </>
            )}

            {/* Stock Tab */}
            {activeTab === 'stock' && (
              <>
                <ExportBar 
                  reportType="low-stock" 
                  dateRange={dateRange}
                  businessId={businessId}
                  reportName="Low Stock Inventory"
                />
                <ReportTable
                  title="Low Stock Items"
                  columns={[
                    { key: 'item_name', label: 'Item', width: '40%' },
                    { key: 'current_stock', label: 'Current', width: '20%', align: 'right' },
                    { key: 'threshold', label: 'Threshold', width: '20%', align: 'right' },
                    { key: 'status', label: 'Status', width: '20%', align: 'center', type: 'status' },
                  ]}
                  rows={lowStock.rows || []}
                  emptyMessage="All items well stocked!"
                  emptyIcon="✅"
                />
              </>
            )}

            {/* Cash & Bank Tab */}
            {activeTab === 'cashbank' && cashBankSummary && (
              <>
                <ExportBar 
                  reportType="cash-bank-summary" 
                  dateRange={dateRange}
                  businessId={businessId}
                  reportName="Cash & Bank Summary"
                />
                <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-6">
                  <div className="grid grid-cols-3 gap-4">
                    <BalanceCard 
                      label="Cash in Hand" 
                      value={cashBankSummary?.totals?.cash_in_hand || 0} 
                      color="emerald"
                      icon={Wallet}
                    />
                    <BalanceCard 
                      label="Bank Balance" 
                      value={cashBankSummary?.totals?.bank_balance || 0} 
                      color="blue"
                      icon={CreditCard}
                    />
                    <BalanceCard 
                      label="Total Balance" 
                      value={cashBankSummary?.totals?.total || 0} 
                      color="purple"
                      icon={DollarSign}
                    />
                  </div>
                </div>
              </>
            )}

            {/* GST Tab */}
            {activeTab === 'gst' && (
              <GSTReportsMenu businessId={businessId} />
            )}

            {/* Expenses Tab */}
            {activeTab === 'expenses' && expenseSummary && (
              <>
                <ExportBar
                  reportType="expense-by-category"
                  dateRange={dateRange}
                  businessId={businessId}
                  reportName="Expense by Category"
                />
                <ReportTable
                  title="Expenses by Category"
                  columns={[
                    { key: 'category', label: 'Category', width: '50%' },
                    { key: 'count', label: 'Entries', width: '25%', align: 'right' },
                    { key: 'amount', label: 'Total Amount', width: '25%', align: 'right', format: 'currency' },
                  ]}
                  rows={expenseSummary.rows || []}
                  emptyMessage="No expense data for selected period"
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Report Table Component
const ReportTable = ({ title, titleColor, columns, rows, emptyMessage, emptyIcon, compact }) => (
  <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
    <div className={`px-4 py-3 border-b border-slate-100 flex items-center gap-2 ${compact ? 'py-2.5' : ''}`}>
      {titleColor && (
        <div className={`w-2 h-2 rounded-full bg-${titleColor}-500`}></div>
      )}
      <h3 className={`text-[13px] font-medium text-slate-700 ${compact ? 'text-[12px]' : ''}`}>{title}</h3>
    </div>
    {rows.length > 0 ? (
      <table className="tl-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th 
                key={col.key} 
                className={`tl-th tl-th-teal ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}
                style={{ width: col.width }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className={`tl-tr hover:bg-teal-50/40 ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}>
              {columns.map(col => (
                <td 
                  key={col.key} 
                  className={`tl-td ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${compact ? 'py-2' : ''}`}
                >
                  {col.type === 'status' ? (
                    <StatusBadge status={row[col.key]} />
                  ) : col.format === 'currency' ? (
                    <span className={`font-medium ${col.colorClass || 'text-slate-800'}`}>
                      ₹{(col.absolute ? Math.abs(row[col.key]) : row[col.key])?.toLocaleString() || 0}
                    </span>
                  ) : col.key === 'date' ? (
                    <span className="text-[13px] text-slate-700">
                      {new Date(row[col.key]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  ) : (
                    <span className="text-[13px] text-slate-700">{row[col.key]}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <div className={`text-center ${compact ? 'py-8' : 'py-12'}`}>
        {emptyIcon && <span className="text-2xl mb-2 block">{emptyIcon}</span>}
        <p className="text-[13px] text-slate-400">{emptyMessage}</p>
      </div>
    )}
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    Critical: { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
    Low: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  };
  const style = styles[status] || styles.Low;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${style.bg} ${style.text} rounded-full text-[10px] font-medium`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
      {status}
    </span>
  );
};

// Balance Card Component
const BalanceCard = ({ label, value, color, icon: Icon }) => {
  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
  };
  return (
    <div className={`p-5 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5" />
        <span className="text-[12px] font-medium uppercase tracking-wider opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">₹{value?.toLocaleString() || 0}</div>
    </div>
  );
};

// GST Reports Menu Component
const GSTReportsMenu = ({ businessId }) => {
  const navigate = useNavigate();

  const gstReports = [
    { title: 'GSTR-1', description: 'Outward supplies', path: '/gst/gstr1', icon: FileText, category: 'Returns' },
    { title: 'GSTR-3B', description: 'Monthly summary', path: '/gst/gstr3b', icon: BarChart3, category: 'Returns' },
    { title: 'HSN Summary', description: 'HSN-wise supplies', path: '/gst/hsn-summary', icon: Package, category: 'Returns' },
    { title: 'Sales Register', description: 'GST sales register', path: '/gst/sales-register', icon: TrendingUp, category: 'Registers' },
    { title: 'Purchase Register', description: 'GST purchase register', path: '/gst/purchase-register', icon: TrendingDown, category: 'Registers' },
    { title: 'GST Summary', description: 'Overall position', path: '/gst/summary', icon: DollarSign, category: 'Analysis' },
    { title: 'ITC Reconciliation', description: 'Reconcile ITC', path: '/gst/itc-reconciliation', icon: ArrowUpRight, category: 'Analysis' },
    { title: 'GSTR-2B', description: 'Auto-drafted ITC statement', path: '/gst/gstr2b', icon: CreditCard, category: 'Analysis' },
    { title: 'Books vs GST', description: 'Discrepancy detection', path: '/gst/books-vs-gst', icon: AlertTriangle, category: 'Analysis' },
    { title: 'ITC Eligibility', description: 'Section 17(5) classification', path: '/gst/itc-eligibility', icon: CheckCircle, category: 'Analysis' },
  ];

  const categories = ['Returns', 'Registers', 'Analysis'];

  return (
    <div className="space-y-4">
      {categories.map(category => (
        <div key={category} className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{category}</h3>
          </div>
          <div className="p-4 grid grid-cols-3 gap-3">
            {gstReports.filter(r => r.category === category).map(report => {
              const Icon = report.icon;
              return (
                <button
                  key={report.path}
                  onClick={() => navigate(`/business/${businessId}${report.path}`)}
                  className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg text-left hover:bg-teal-50 hover:border-teal-200 border border-transparent transition-all group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center group-hover:bg-teal-100">
                    <Icon className="w-4 h-4 text-slate-500 group-hover:text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-slate-700 group-hover:text-teal-700">{report.title}</h4>
                    <p className="text-[11px] text-slate-400 mt-0.5">{report.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReportsDashboard;
